import { Controller, useFormContext } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { TiposEntradas, useGetIncidenciaByFiltersLazyQuery } from "src/gql/schema"
import { InputText } from "src/shared/components/forms"
import InputTabs from "src/shared/components/forms/input-tabs/InputTabs"
import Counter from "src/shared/components/forms/counter/Counter"
import Icon from "src/shared/icons"
import { useProfile } from "src/shared/hooks/useProfile"
import { getCurrencyFormat } from "src/utils/string"
import { RootState } from "src/store/store"
import FormSection from "../../components/FormSection/FormSection"
import FormSectionRow from "../../components/FormSectionRow/FormSectionRow"
import ModalMatricula from "../ModalMatricula/ModalMatricula"
import { entryTypes, inputIconStyle } from "../../VentaHabitacion.constants"
import { DefaultValuesType } from "../../VentaHabitacion.types"

interface HuespedProps {
    // La reserva fija las personas extra
    isFromReserva: boolean
    personasIncluidas?: number | null
    personasMinimas: number
    personasExtraMinimas: number
    personasBloqueadas: boolean
    nombreBloqueado: boolean
    // El estado del modal de matrícula se comparte con la tecla escape del formulario
    isModalMatriculaOpen: boolean
    isModalMatriculaOpenFromState: "canceled" | "toEdit"
    setIsModalMatriculaOpen: (value: boolean) => void
    setIsModalMatriculaOpenFromState: (value: "canceled" | "toEdit") => void
}

// Nombre del huésped, tipo de entrada con matrícula, personas y personas extra
const Huesped = ({
    isFromReserva,
    personasIncluidas,
    personasMinimas,
    personasExtraMinimas,
    personasBloqueadas,
    nombreBloqueado,
    isModalMatriculaOpen,
    isModalMatriculaOpenFromState,
    setIsModalMatriculaOpen,
    setIsModalMatriculaOpenFromState,
}: HuespedProps) => {
    const { control, watch, getValues, setValue, formState } = useFormContext<DefaultValuesType>()
    const navigate = useNavigate()
    const { hotel_id } = useProfile()
    const { tarifaSeleccionada } = useSelector((root: RootState) => root.ventaHabitacion)
    const [getIncidenciasHuesped, { loading, data: incidenciasHuesped }] = useGetIncidenciaByFiltersLazyQuery()
    const [getIncidenciasMatricula, { data: incidenciasMatricula }] = useGetIncidenciaByFiltersLazyQuery()

    const nombreHuesped = watch("name")
    const matricula = watch("matricula")
    const modeloAuto = watch("modelo")
    const extraUsers = watch("extraUsers")

    return (
        <FormSection title="Huésped">
            <FormSectionRow>
                <Controller
                    control={control}
                    name={"name"}
                    render={({ field: { value, onChange } }) => (
                        <InputText
                            icon={Icon}
                            iconProps={{
                                name: "userFilled",
                                ...inputIconStyle,
                            }}
                            inputWrapperClass="venta-habitacion-input"
                            label={"Nombre completo (Opcional)"}
                            type={"text"}
                            placeholder={"Escribe el nombre"}
                            value={value}
                            disabled={nombreBloqueado}
                            onChange={(e) => onChange(e.target.value)}
                            onKeyUp={(e) => {
                                if (e.key === "Enter") e.preventDefault()
                            }}
                            loader={loading}
                            onBlur={() => {
                                getIncidenciasHuesped({
                                    variables: {
                                        hotel_id,
                                        matricula: null,
                                        huesped: nombreHuesped || null,
                                    },
                                })
                            }}
                            error={!!nombreHuesped && !!incidenciasHuesped?.incidencias[0]}
                            errorhinttext={
                                "Huésped con reporte de incidencia. Más detalle en el módulo de incidencias."
                            }
                        />
                    )}
                />
                <Controller
                    control={control}
                    name={"entryType"}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                        <InputTabs
                            className="venta-habitacion-input"
                            label={"Tipo de entrada"}
                            items={entryTypes}
                            value={value}
                            onChange={onChange}
                        />
                    )}
                />
                <ModalMatricula
                    isOpenFromState={isModalMatriculaOpenFromState}
                    isOpen={isModalMatriculaOpen}
                    onClose={() => {
                        setIsModalMatriculaOpen(false)
                        getIncidenciasMatricula({
                            variables: {
                                hotel_id,
                                matricula: matricula || null,
                                huesped: null,
                            },
                        })
                    }}
                    onCancel={() => {
                        setValue("entryType", entryTypes[0].value as TiposEntradas)
                        setIsModalMatriculaOpen(false)
                    }}
                />
                {!!matricula && (
                    <div>
                        <div className="venta-h-screen__edit-matricula__wrapper">
                            <span
                                onClick={() => {
                                    setIsModalMatriculaOpen(true)
                                    setIsModalMatriculaOpenFromState("toEdit")
                                }}
                                className="venta-h-screen__edit-matricula"
                            >
                                {modeloAuto} {matricula}
                            </span>
                            <Icon name="pencil" color="var(--primary)" width={20} height={20} />
                        </div>
                        {incidenciasMatricula?.incidencias?.[0] ? (
                            <div>
                                <p className="venta-h-screen__edit-matricula--error">
                                    {"Esta matrícula cuenta con reporte de incidencia."}
                                </p>
                                <p className="venta-h-screen__edit-matricula--error">
                                    {"Consulta"}
                                    <span
                                        onClick={() =>
                                            navigate(`/u/incidencias-matricula/${matricula}`, {
                                                state: { modelo: modeloAuto },
                                            })
                                        }
                                    >
                                        {"aquí"}
                                    </span>
                                    {"para saber más."}
                                </p>
                            </div>
                        ) : null}
                    </div>
                )}
            </FormSectionRow>
            <FormSectionRow>
                <Controller
                    control={control}
                    name={"users"}
                    rules={{ required: true, min: 1 }}
                    render={({ field: { value, onChange } }) => (
                        <div className="venta-h-screen__count">
                            <p className="venta-h-screen__count__label">Personas</p>
                            <Counter
                                title="personas"
                                style={{ marginRight: "40px" }}
                                className="venta-h-screen__counter"
                                max={personasIncluidas || 0}
                                disabled={false}
                                min={personasMinimas}
                                value={value}
                                onClick={(lvalue) => {
                                    const valueNumPersonasExtra = getValues("extraUsers") || 0
                                    if (valueNumPersonasExtra > 0 && lvalue < value) {
                                        setValue("extraUsers", valueNumPersonasExtra - 1)
                                        onChange(value)
                                    } else {
                                        onChange(lvalue)
                                    }
                                }}
                                disable={personasBloqueadas || !personasIncluidas || Number(personasIncluidas) === 0}
                                errorHintText={formState?.errors?.users ? "*El número de personas es requerido" : ""}
                            />
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name={"extraUsers"}
                    rules={{ required: false, min: 0 }}
                    render={({ field: { value, onChange } }) => (
                        <div className="venta-h-screen__count">
                            <p className="venta-h-screen__count__label">
                                Personas extra
                                {tarifaSeleccionada !== null ? (
                                    <span>{`(${getCurrencyFormat(tarifaSeleccionada?.costo_persona_extra)} c/u)`}</span>
                                ) : null}
                            </p>
                            <Counter
                                title="Personas extra"
                                className="venta-h-screen__counter"
                                longTextLimit={
                                    tarifaSeleccionada &&
                                    tarifaSeleccionada.personas_extra_max &&
                                    extraUsers > tarifaSeleccionada?.personas_extra_max
                                        ? "Límite de personas extra superado, necesitarás PIN para vender"
                                        : ""
                                }
                                //max={tarifaSeleccionada?.personas_extra_max || 0}
                                min={personasExtraMinimas}
                                value={value}
                                onClick={(lvalue) => {
                                    const valueNumPersonas = getValues("users") || 0
                                    const numMaxPersonas = personasIncluidas || 0
                                    if (valueNumPersonas >= numMaxPersonas) {
                                        onChange(lvalue)
                                    } else if (lvalue > (value || 0)) {
                                        setValue("users", valueNumPersonas + lvalue)
                                    }
                                }}
                                disable={
                                    !tarifaSeleccionada?.personas_extra_max ||
                                    Number(tarifaSeleccionada?.personas_extra_max) === 0 ||
                                    isFromReserva
                                }
                            />
                        </div>
                    )}
                />
            </FormSectionRow>
        </FormSection>
    )
}

export default Huesped
